import { useEffect, useState } from "react";
import { Settings, Mail, KeyRound, Loader2, AlertTriangle, CheckCircle2, Send } from "lucide-react";
import { api, formatApiError, ADMIN_TOKEN_KEY } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const fieldCls =
  "h-12 rounded-xl border-brand-line focus-visible:ring-brand-red/30 focus-visible:border-brand-navy";

export function AccountDialog({ user, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [notifyEmail, setNotifyEmail] = useState("");
  const [alertStatus, setAlertStatus] = useState("unknown");
  const [alertError, setAlertError] = useState(null);
  const [testing, setTesting] = useState(false);
  const [currentEmail, setCurrentEmail] = useState(user?.email || "");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setNewEmail("");
    setNewPassword("");
    setConfirmPassword("");
    setCurrentPassword("");
    setLoading(true);
    api.get("/admin/account")
      .then(({ data }) => {
        setNotifyEmail(data.notification_email || "");
        setCurrentEmail(data.email || "");
        setAlertStatus(data.alert_email_status || "unknown");
        setAlertError(data.alert_email_last_error || null);
      })
      .catch((err) => setError(formatApiError(err.response?.data?.detail, "Could not load account settings.")))
      .finally(() => setLoading(false));
  }, [open]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/account/test-alert");
      setAlertStatus(data.ok ? "ok" : "bouncing");
      setAlertError(data.ok ? null : data.error);
      if (data.ok) toast.success(`Test alert delivered to ${data.recipient}`);
      else toast.error(`Test alert bounced — ${data.recipient} is not receiving mail`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not send test alert."));
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setError("");
    const body = {};
    if (notifyEmail.trim()) body.notification_email = notifyEmail.trim();
    if (newEmail.trim()) body.new_email = newEmail.trim();
    if (newPassword) {
      if (newPassword.length < 8) {
        setError("New password must be at least 8 characters.");
        return;
      }
      if (newPassword !== confirmPassword) {
        setError("New password and confirmation do not match.");
        return;
      }
      body.new_password = newPassword;
    }
    const changingCreds = body.new_email || body.new_password;
    if (changingCreds && !currentPassword) {
      setError("Enter your current password to change your email or password.");
      return;
    }
    if (changingCreds) body.current_password = currentPassword;
    if (Object.keys(body).length === 0) {
      setError("Nothing to update.");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.patch("/admin/account", body);
      if (data.access_token) localStorage.setItem(ADMIN_TOKEN_KEY, data.access_token);
      onUpdated?.({ email: data.email });
      toast.success(changingCreds ? "Account updated" : "Notification email saved");
      setOpen(false);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail, "Could not update account.");
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="admin-settings-btn"
          className="btn-focus grid place-items-center w-11 h-11 rounded-xl border border-brand-line text-brand-navy hover:bg-brand-bg transition-colors"
          title="Account settings"
        >
          <Settings className="w-5 h-5" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="account-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-brand-navy">Account settings</DialogTitle>
          <DialogDescription className="text-brand-ink2">
            Manage where owner alerts are sent and update your login email or password.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 grid place-items-center">
            <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Notification / alert recipient */}
            <div className="space-y-2">
              <Label className="text-brand-navy font-semibold flex items-center gap-2">
                <Mail className="w-4 h-4 text-brand-red" /> Alert recipient email
              </Label>
              {alertStatus === "bouncing" && (
                <div className="rounded-xl bg-brand-red/8 border border-brand-red/25 p-3 flex items-start gap-2.5" data-testid="alert-bounce-banner">
                  <AlertTriangle className="w-5 h-5 text-brand-red shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-bold text-brand-red">Your alerts aren&apos;t landing.</p>
                    <p className="text-brand-ink2">
                      The provider is rejecting mail to this address as undeliverable, so lead &amp; order
                      alerts are bouncing. Point it at an inbox you can actually receive at, then send a test.
                    </p>
                  </div>
                </div>
              )}
              {alertStatus === "ok" && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2.5" data-testid="alert-ok-banner">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  <p className="text-sm font-semibold text-emerald-700">Alerts are delivering to this inbox.</p>
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  data-testid="account-notification-email"
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`flex-1 ${fieldCls}`}
                />
                <button
                  type="button"
                  onClick={sendTest}
                  disabled={testing || !notifyEmail.trim()}
                  data-testid="account-test-alert"
                  className="btn-focus inline-flex items-center justify-center gap-2 h-12 px-4 rounded-xl border border-brand-line font-bold text-brand-navy hover:bg-brand-bg transition-colors disabled:opacity-50 whitespace-nowrap"
                  title="Send a test alert to this address"
                >
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 text-brand-red" />}
                  Test
                </button>
              </div>
              <p className="text-xs text-brand-ink2">
                New quote requests and paid orders are emailed here. Use an inbox you can actually
                receive at so alerts start landing right away. Tip: save a new address first, then send a test.
              </p>
            </div>

            <div className="h-px bg-brand-line" />

            {/* Credentials */}
            <div className="space-y-4">
              <p className="text-brand-navy font-bold flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-brand-red" /> Change login credentials
              </p>
              <div className="space-y-2">
                <Label className="text-brand-navy font-semibold">Login email</Label>
                <Input
                  data-testid="account-new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder={currentEmail}
                  className={fieldCls}
                />
                <p className="text-xs text-brand-ink2">Leave blank to keep {currentEmail}.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-brand-navy font-semibold">New password</Label>
                  <Input
                    data-testid="account-new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    className={fieldCls}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-brand-navy font-semibold">Confirm password</Label>
                  <Input
                    data-testid="account-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    className={fieldCls}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-brand-navy font-semibold">Current password</Label>
                <Input
                  data-testid="account-current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Required to change email or password"
                  className={fieldCls}
                />
              </div>
            </div>

            {error && (
              <p className="text-brand-red font-semibold text-sm" data-testid="account-error">{error}</p>
            )}
          </div>
        )}

        <DialogFooter>
          <button
            onClick={save}
            disabled={saving || loading}
            data-testid="account-save"
            className="btn-focus inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-brand-red text-white font-bold hover:bg-brand-red-600 transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
